import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How We Used Webpack to Reduce Our JS Footprint by 50",
  "description": "",
  "published": "2017-02-10T00:00:00.000Z",
  "redirect_from": ["/blog/webpack-reduce-js-footprint/swizec/7424"],
  "categories": "Front End, Technical, Webpack",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "wIh7xTBZbow",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`In this article, I'm going to show you how we went `}<strong parentName="p">{`from 30 requests for 3.1MB`}</strong>{` of minified uncompressed JavaScript `}<strong parentName="p">{`to 19 requests for 2.2MB`}</strong>{` of minified uncompressed JS. Why uncompressed? Because Rails doesn't gzip on localhost, and our production servers are running the new setup already.`}</p>
    <p>{`We weren't doing anything stupid before. Tree shaking, minification, code splitting to avoid JavaScript users don't need… we had all of that. We even split our frontend into 5 or 6 discreet apps, each with its own internal code splitting.`}</p>
    <p>{`And yet, 30 requests for 3.1MB.`}</p>
    <p>{`Our problem: Third-party libraries. There's a bunch of stuff that we need, like Backbone and Handlebars and Lodash and jQuery and so on. Most of them we loaded from public CDN. Some of them we bundled locally into a `}<inlineCode parentName="p">{`vendor.js`}</inlineCode>{` file; this is where the huge size came from.`}</p>
    <p>{`You see, when all your apps share a `}<inlineCode parentName="p">{`vendor.js`}</inlineCode>{` file, and some of them are React and some are Backbone, guess what happens? All apps load both React `}<em parentName="p">{`and`}</em>{` Backbone.`}</p>
    <p>{`We achieved this vendor/app split following `}<a parentName="p" {...{
        "href": "https://webpack.js.org/guides/code-splitting-libraries/"
      }}>{`Webpack's official guide on code splitting libraries`}</a>{`. It suggests using the `}<inlineCode parentName="p">{`CommonChunksPlugin`}</inlineCode>{` to extract common code into a top-level file.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`plugins: [
  // Avoid publishing files when compilation failed:
  new webpack.NoEmitOnErrorsPlugin(),
  new ExtractTextPlugin(
    envConfig.inDevelopment()
      ? "[name]_style.css"
      : "[name]_style.[chunkhash].css"
  ),
  new webpack.optimize.CommonsChunkPlugin({
    names: ["vendor", "manifest"],
  }),
];
`}</code></pre>
    <p><inlineCode parentName="p">{`manifest.js`}</inlineCode>{` is meant to contain Webpack's runtime, and `}<inlineCode parentName="p">{`vendor.js`}</inlineCode>{` contains your more stable third-party dependencies. This is meant to improve caching. `}<em parentName="p">{`shrug`}</em></p>
    <p>{`On top of that, we had a bunch of global libraries configured as externals and loaded them in top-level `}<inlineCode parentName="p">{`<script>`}</inlineCode>{` tags. Not a bad approach per se, but something like the AWS SDK is almost 500kb of `}<em parentName="p">{`compressed`}</em>{` JavaScript. When you're using only one function… yeah, no bueno.`}</p>
    <p>{`We fixed this situation with a 2-pronged approach:`}</p>
    <ul>
      <li parentName="ul">{`bundle and tree shake all our dependencies ourselves`}</li>
      <li parentName="ul">{`create a different vendor file for each app (entry file)`}</li>
    </ul>
    <p>{`Here's how that looks:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const Apps = {
  // list entries
  // will be reused as Webpack's entry config
};

// previously loaded as externals
const GlobalModules = _.map(
  {
    jquery: ["$", "jQuery", "window.jQuery"],
    lodash: ["_"],
    backbone: ["Backbone"],
    "backbone-validation": ["Backbone.Validation"],
    "raven-js": ["Raven"],
    moment: ["moment"],
    string: ["string", "S"],
    async: ["async"],
  },
  (vars, module) =>
    new webpack.ProvidePlugin(_.fromPairs(vars.map((v) => [v, module])))
);

// updated plugins config
plugins: [
  new webpack.NoEmitOnErrorsPlugin(),
  new ExtractTextPlugin(
    envConfig.inDevelopment()
      ? "[name]_style.css"
      : "[name]_style.[chunkhash].css"
  ),
]
  .concat(GlobalModules)
  .concat(
    Object.keys(Apps).map(
      (app) =>
        new webpack.optimize.CommonsChunkPlugin({
          name: \`\${app}_vendor\`,
          chunks: [app],
          minChunks: isVendor,
        })
    )
  )
  .concat([
    new webpack.optimize.CommonsChunkPlugin({
      name: "manifest",
      chunks: Object.keys(Apps).map((n) => \`\${n}_vendor\`),
      minChunks: (module, count) => {
        return count >= Object.keys(Apps).length && isVendor(module);
      },
    }),
  ]);
`}</code></pre>
    <p>{`All our `}<inlineCode parentName="p">{`entry`}</inlineCode>{` files, or apps as we call them, go in `}<inlineCode parentName="p">{`Apps`}</inlineCode>{`. This helps us iterate over them when building the `}<inlineCode parentName="p">{`plugins`}</inlineCode>{` config.`}</p>
    <p>{`What used to be externals loaded in global `}<inlineCode parentName="p">{`script`}</inlineCode>{` tags become `}<inlineCode parentName="p">{`GlobalModules`}</inlineCode>{`. Each is translated into a `}<inlineCode parentName="p">{`ProvidePlugin`}</inlineCode>{` configuration, which essentially replaces all occurrences of `}<inlineCode parentName="p">{`$`}</inlineCode>{` with `}<inlineCode parentName="p">{`require('jquery')`}</inlineCode>{`, `}<inlineCode parentName="p">{`moment`}</inlineCode>{` with `}<inlineCode parentName="p">{`require('moment')`}</inlineCode>{`, and so on.`}</p>
    <p>{`With this approach, we don't have to change any code that relies on global libs' availability.`}</p>
    <p>{`Armed with these vars, we dynamically generate a list of Webpack `}<inlineCode parentName="p">{`plugins`}</inlineCode>{`. Each app gets its own vendor file – `}<inlineCode parentName="p">{`_vendor.js`}</inlineCode>{` – and at the end, all files together get a common `}<inlineCode parentName="p">{`manifest`}</inlineCode>{` file with the Webpack runtime. Again, to prevent cache churning.`}</p>
    <p>{`Oh, and there's the helpful `}<inlineCode parentName="p">{`isVendor`}</inlineCode>{` function, which I got from `}<a parentName="p" {...{
        "href": "https://leanpub.com/survivejs-webpack"
      }}>{`Juho's SurviveJS - Webpack`}</a>{` book. You should buy it. It's great.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function isVendor(module, count) {
  const userRequest = module.userRequest;

  return userRequest && userRequest.indexOf("node_modules") >= 0;
}
`}</code></pre>
    <p>{`This function tells us if a specific module is a third-party library or our own code. It's third party if it's in `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{`, and our own code if it's not.`}</p>
    <p>{`I hope this helps make your website faster. It got our page speed score from 54 to 95 ?`}</p>
    <p>{`PS: This article is a spiritual successor to `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/using-webpack-2-production/swizec/7356"
      }}>{`Migrating to Webpack 2: some tips and gotchas`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      